// src/components/form/VendorKyc.jsx

import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as UserServices from "../../Services/UserServices";
import * as Yup from "yup";
import * as Alert from "../common/Alert";
import { useNavigate, useLocation } from "react-router-dom";
import { GiCheckMark } from "react-icons/gi";
import { MdArrowBackIosNew } from "react-icons/md";
import loginBanner from "../../assets/banner/loginBanner.png";
import { useDispatch, useSelector } from "react-redux";
import {
  setKycFile,
  clearKycFile,
} from "../../redux/reducers/vendorKycReducer";
import { clearVendorDraft } from "../../redux/reducers/vendorRegistrationReducer";

const STORAGE_KEYS = {
  aadharCard: "kyc_aadharCard",
  gstCertificate: "kyc_gstCertificate",
  accountCheque: "kyc_accountCheque",
  shopLicense: "kyc_shopLicense",
  otherDocs: "kyc_otherDocs",
};

const VendorKyc = () => {
  const [loading, setLoading] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  const [initialMobile, setInitialMobile] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Pull persisted file data from Redux (for completeness, though we use sessionStorage)
  const persisted = useSelector((state) => state.vendorKyc.file);

  // 1) On mount: grab vendor_id from location.state or fallback to localStorage
  useEffect(() => {
    const { vendor_id: passedVendorId, mobile_no: passedMobile } =
      location.state || {};

    if (passedVendorId) {
      setVendorId(passedVendorId);
      setInitialMobile(passedMobile);
      localStorage.setItem("vendor_id", passedVendorId);
    } else {
      const id = localStorage.getItem("vendor_id");
      if (id || passedMobile) {
        setVendorId(id);
        // Also capture the passed mobile number (if any) so we can display or use it
        if (passedMobile) {
          setInitialMobile(passedMobile);
        }
      } else {
        Alert.Alert.ErrorAlert(
          "Vendor Details not found. Please register first."
        );
        navigate("/vendor-registration");
        return;
      }
    }
  }, [location.state, navigate]);

  // 2) Build initialValues for Formik by checking sessionStorage
  const buildInitialValues = () => {
    const iv = {
      aadharCard: null,
      gstCertificate: null,
      accountCheque: null,
      shopLicense: null,
      otherDocs: null,
    };

    Object.entries(STORAGE_KEYS).forEach(([fieldName, storageKey]) => {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      try {
        const { dataUrl, filename } = JSON.parse(raw);
        if (dataUrl && filename) {
          // Convert dataURL back into a File object
          const [header, base64String] = dataUrl.split(",");
          const mimeMatch = header.match(/:(.*?);/);
          const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
          const binary = atob(base64String);
          let n = binary.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = binary.charCodeAt(n);
          }
          iv[fieldName] = new File([u8arr], filename, { type: mime });
        }
      } catch (e) {}
    });

    return iv;
  };

  // 3) Formik for KYC (upload documents), with enableReinitialize
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: buildInitialValues(),
    validationSchema: Yup.object().test(
      "at-least-one",
      "At least one document is required",
      (values) =>
        !!(
          values.aadharCard ||
          values.gstCertificate ||
          values.accountCheque ||
          values.shopLicense ||
          values.otherDocs
        )
    ),
    onSubmit: async (values) => {
      if (!vendorId && !initialMobile) {
        Alert.Alert.ErrorAlert(
          "Vendor Details missing. Please register again."
        );
        navigate("/vendor-registration");
        return;
      }

      const documents = [
        { name: "aadharCard", label: "Aadhar Card" },
        { name: "gstCertificate", label: "GST Certificate" },
        { name: "accountCheque", label: "Current Account Cheque" },
        { name: "shopLicense", label: "Shop Establishment License" },
        { name: "otherDocs", label: "Other Shop Documents" },
      ];

      try {
        const kycDocuments = [];
        for (let doc of documents) {
          if (!values[doc.name]) continue;

          kycDocuments.push({
            vendor_id: vendorId,
            doc_name: doc.label,
            doc_path: values[doc.name],
          });
        }

        if (kycDocuments.length === 0) {
          Alert.Alert.ErrorAlert("Please upload at least one document.");
          return;
        }

        const formData = new FormData();

        const metadata = [];

        for (let i = 0; i < kycDocuments.length; i++) {
          const doc = kycDocuments[i];

          // Append file with indexed key (or just the doc_name)
          formData.append(`file`, doc.doc_path); // you can use `files[]` if your backend expects it

          // Build metadata entry
          metadata.push({
            vendor_id: doc.vendor_id,
            doc_name: doc.doc_name,
            doc_path: doc.doc_path.name, // just the filename here, not the file itself
          });
        }

        // Append metadata as JSON string
        formData.append("metadata", JSON.stringify(metadata));

        const res = await UserServices.vendorKYC(formData);

        if (!(res.status === 200 || res.status === 201)) {
          throw new Error(`Failed to upload document`);
        }

        Alert.Alert.SuccessAlert("KYC document(s) uploaded successfully!");
        localStorage.removeItem("vendor_id");

        // 🚩 Clear the registration draft now that KYC is done
        dispatch(clearVendorDraft());

        // Clear all persisted files on success:
        Object.values(STORAGE_KEYS).forEach((key) =>
          sessionStorage.removeItem(key)
        );
        dispatch(clearKycFile());
        navigate("/");
      } catch (error) {
        const msg = error?.response?.data?.message || "Something went wrong";
        Alert.Alert.ErrorAlert(msg);
      }
    },
  });

  // 4) When the user picks a file: read it as DataURL, store in sessionStorage + Redux + Formik
  const handleFileChange = (event, fieldName) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result; // "data:...;base64,...."
      // Persist into sessionStorage
      sessionStorage.setItem(
        STORAGE_KEYS[fieldName],
        JSON.stringify({ dataUrl, filename: file.name })
      );
      // Optionally persist into Redux as well:
      dispatch(setKycFile({ dataUrl, filename: file.name }));
      // Update Formik field
      formik.setFieldValue(fieldName, file);
    };
    reader.readAsDataURL(file);
  };

  // 5) If the user clicks "Remove", clear that one field from sessionStorage, Redux, and Formik
  const handleRemove = (fieldName) => {
    sessionStorage.removeItem(STORAGE_KEYS[fieldName]);
    dispatch(clearKycFile());
    formik.setFieldValue(fieldName, null);
  };

  return (
    <div className="flex flex-col lg:flex-row h-[88vh]">
      <div className="w-100 lg:w-1/2 flex justify-center">
        <img
          loading="lazy"
          src={loginBanner}
          alt="banner"
          className={`h-[88vh] lg:w-[50vw]`}
        />
      </div>

      <div className="w-100 lg:w-1/2 px-[5vh] md:px-[10vh] lg:px-[15vh] lg:h-[88vh] lg:overflow-y-auto">
        <div className="w-100 lg:mx-5 pb-3 mt-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h6 className="fw-bold text-xl md:text-2xl lg:text-4xl">KYC</h6>
              <h2 className="fw-bold text-base md:text-lg lg:text-2xl">
                Upload at least one document to complete KYC process.
              </h2>
            </div>
            <button
              className="text-black font-semibold flex items-center gap-1"
              onClick={() => navigate(-1)}
            >
              <MdArrowBackIosNew /> Back
            </button>
          </div>

          {/* If we received a mobile number via router state, show it here */}
          {initialMobile && (
            <div className="mt-3">
              <label className="text-lg lg:text-xl">
                Registered Mobile Number
              </label>
              <input
                type="text"
                value={initialMobile}
                readOnly
                className="form-control mt-1 bg-gray-100"
              />
            </div>
          )}

          <div className="shadow-md rounded-lg p-2 px-4 mt-3 bg-gray-100">
            <p>
              <b>Note:</b> Certificate must clearly show the following things
            </p>
            <p className="flex flex-col xl:flex-row justify-between mt-1">
              <span className="flex items-center gap-1">
                <GiCheckMark className="text-green-700 fw-bold" /> Legal Name
              </span>
              <span className="flex items-center gap-1">
                <GiCheckMark className="text-green-700 fw-bold" /> Registration
                Number
              </span>
              <span className="flex items-center gap-1">
                <GiCheckMark className="text-green-700 fw-bold" /> Trade Name
              </span>
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className="mt-3">
            {[
              { label: "Aadhar Card", name: "aadharCard" },
              { label: "GST Certificate", name: "gstCertificate" },
              { label: "Current Account Cheque", name: "accountCheque" },
              { label: "Shop Establishment License", name: "shopLicense" },
              { label: "Other Shop Documents", name: "otherDocs" },
            ].map(({ label, name }) => {
              const fileObj = formik.values[name]; // Either a File or null

              return (
                <div className="mt-3" key={name}>
                  <label className="text-lg lg:text-xl">{label}</label>
                  {fileObj ? (
                    <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-gray-800">{fileObj.name}</span>
                      <button
                        type="button"
                        className="text-red-600 underline text-sm"
                        onClick={() => handleRemove(name)}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      className="form-control mt-1"
                      onChange={(e) => handleFileChange(e, name)}
                      onBlur={formik.handleBlur}
                    />
                  )}

                  {formik.touched[name] && formik.errors[name] && (
                    <p className="text-danger text-sm px-2">
                      {formik.errors[name]}
                    </p>
                  )}
                </div>
              );
            })}

            <div className="mt-4">
              <button
                type="submit"
                disabled={loading}
                className="btn theme-outline-btn w-100"
              >
                {loading ? (
                  <div className="flex justify-center items-center gap-2">
                    <span className="animate-spin rounded-full h-5 w-5 border-t-2 border-blue-500"></span>
                    Uploading...
                  </div>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VendorKyc;
